/**
 * Marketing Hub Discovery FILTER widget v2 — faceted collection pages (the AI Filter pillar).
 *
 * BRAND/THEME PORTABILITY: everything store-specific comes from the shop metafield
 * custom.amh_filter — `hosts` (host → brand-qualified market key), `facetsSel` (the theme's
 * facets column), `resultsSel` (the container that changes between filter states) and `killSel`
 * (extra native filter UI to hide). Omit them all and the behaviour is byte-for-byte the
 * ARTMiE/Horizon original.
 *
 * ARCHITECTURE (v2 — "never touch the products"):
 *   • The product grid stays 100% THEME-NATIVE. We never render product cards. On every filter
 *     change the widget fetches the SAME page through Shopify's Section Rendering API
 *     (?section_id=…) with native filter params (filter.p.m.*, filter.v.price.*,
 *     filter.v.availability) and swaps the section's HTML in place — so cards, reviews, variant
 *     quick-add popups, promo badges and hover behavior are the theme's own, pixel-identical.
 *   • We replace ONLY the facet sidebar: our tree (from /api/public/filter, Typesense-powered —
 *     live counts, swatches, value search) renders in the theme's own facets column, in a Shadow
 *     DOM. Each tree option carries `nativeParam` from the Hub so selections translate 1:1 into
 *     native filter URL params (shareable URLs, server-rendered on first load, SEO-safe).
 *   • Mobile: a floating "Filtri" button opens our drawer; the games launcher (.amhg-fab) is
 *     hidden while the drawer is open so nothing overlaps the last filter groups.
 *   • Fail-safe: if the tree API fails on first load, native facets are restored untouched.
 */
(function () {
  var CFG = window.AMH_FILTER || {};
  if (!CFG.api || !Array.isArray(CFG.markets) || !CFG.markets.length) return;
  var HOST_MARKET = CFG.hosts || { "artmie.sk": "SK", "artmie.si": "SI", "artmie.gr": "GR", "artmie.hr": "HR", "artmie.it": "IT", "artmie.de": "DE", "artmie.at": "AT", "artmie.bg": "BG", "artmie.nl": "NL", "artmie.cz": "CZ", "artmie.hu": "HU", "artmie.ro": "RO", "artmie.rs": "RS", "artmie.ba": "BA", "artmie.mk": "MK", "artmie.pl": "PL" };
  var host = location.hostname.replace(/^www\./, "");
  var market = HOST_MARKET[host];
  // AMH_SUBFOLDER_MARKET 2026-10-06: a market on a subfolder of the primary domain (/en-ie/…) is named by the path
  var _pm = location.pathname.match(/^\/[a-z]{2}-([a-z]{2})(?:\/|$)/);
  if (_pm) { var _pk = (CFG.paths || {})[_pm[1]] || _pm[1]; if (_pk) market = _pk; }
  if (!market || CFG.markets.indexOf(market) === -1) return;
  // `market` is the brand-qualified API key ("SK", "pap_sk"); language/currency are country facts.
  var CC = String(market).replace(/^pap_/i, "").toUpperCase();
  var api = String(CFG.api).replace(/\/+$/, "");

  // ── Page context (re-resolved on every soft navigation — the theme morphs between pages) ──
  var COLLECTION = null, sectionEl = null, SECTION_ID = "", active = false, disabled = false;
  function collectionFromPath() {
    var pm = location.pathname.match(/\/collections\/([a-z0-9][a-z0-9-]*)\/?$/);
    return pm ? pm[1] : null;
  }
  var FACETS_SEL = CFG.facetsSel || ".facets-block-wrapper";
  function facetsCol() { return document.querySelector(FACETS_SEL); }
  // The element that actually changes between filter states (verified by diffing section renders:
  // everything outside it is static or hidden native-facets markup).
  var RESULTS_SEL = CFG.resultsSel || "#ResultsList, .main-collection-grid, #ProductGridContainer";

  // ── i18n (UI chrome only — products are native) ──
  var I18N = {
    nl: { filters: "Filters", clearAll: "Alles wissen", clear: "Wissen", showMore: "Meer tonen", showLess: "Minder tonen", searchValues: "Zoeken…", close: "Sluiten", from: "Van", to: "Tot", inStock: "Op voorraad", show: "Tonen" },
    en: { filters: "Filters", clearAll: "Clear all", clear: "Clear", showMore: "Show more", showLess: "Show less", searchValues: "Search…", close: "Close", from: "From", to: "To", inStock: "In stock", show: "Show" },
    de: { filters: "Filter", clearAll: "Alles löschen", clear: "Löschen", showMore: "Mehr anzeigen", showLess: "Weniger anzeigen", searchValues: "Suchen…", close: "Schließen", from: "Von", to: "Bis", inStock: "Auf Lager", show: "Anzeigen" },
    sk: { filters: "Filtre", clearAll: "Zrušiť všetko", clear: "Zrušiť", showMore: "Zobraziť viac", showLess: "Zobraziť menej", searchValues: "Hľadať…", close: "Zavrieť", from: "Od", to: "Do", inStock: "Skladom", show: "Zobraziť" },
    sl: { filters: "Filtri", clearAll: "Počisti vse", clear: "Počisti", showMore: "Prikaži več", showLess: "Prikaži manj", searchValues: "Išči…", close: "Zapri", from: "Od", to: "Do", inStock: "Na zalogi", show: "Prikaži" },
    el: { filters: "Φίλτρα", clearAll: "Καθαρισμός όλων", clear: "Καθαρισμός", showMore: "Περισσότερα", showLess: "Λιγότερα", searchValues: "Αναζήτηση…", close: "Κλείσιμο", from: "Από", to: "Έως", inStock: "Σε απόθεμα", show: "Εμφάνιση" },
    hr: { filters: "Filtri", clearAll: "Očisti sve", clear: "Očisti", showMore: "Prikaži više", showLess: "Prikaži manje", searchValues: "Traži…", close: "Zatvori", from: "Od", to: "Do", inStock: "Na zalihi", show: "Prikaži" },
    it: { filters: "Filtri", clearAll: "Cancella tutto", clear: "Cancella", showMore: "Mostra di più", showLess: "Mostra meno", searchValues: "Cerca…", close: "Chiudi", from: "Da", to: "A", inStock: "Disponibile", show: "Mostra" },
    bg: { filters: "Филтри", clearAll: "Изчисти всички", clear: "Изчисти", showMore: "Покажи повече", showLess: "Покажи по-малко", searchValues: "Търсене…", close: "Затвори", from: "От", to: "До", inStock: "В наличност", show: "Покажи" },
    cs: { filters: "Filtry", clearAll: "Zrušit vše", clear: "Zrušit", showMore: "Zobrazit více", showLess: "Zobrazit méně", searchValues: "Hledat…", close: "Zavřít", from: "Od", to: "Do", inStock: "Skladem", show: "Zobrazit" },
    hu: { filters: "Szűrők", clearAll: "Összes törlése", clear: "Törlés", showMore: "Több megjelenítése", showLess: "Kevesebb", searchValues: "Keresés…", close: "Bezárás", from: "Tól", to: "Ig", inStock: "Készleten", show: "Megjelenítés" },
    ro: { filters: "Filtre", clearAll: "Șterge tot", clear: "Șterge", showMore: "Arată mai mult", showLess: "Arată mai puțin", searchValues: "Caută…", close: "Închide", from: "De la", to: "Până la", inStock: "În stoc", show: "Afișează" },
    sr: { filters: "Filteri", clearAll: "Očisti sve", clear: "Očisti", showMore: "Prikaži više", showLess: "Prikaži manje", searchValues: "Pretraži…", close: "Zatvori", from: "Od", to: "Do", inStock: "Na stanju", show: "Prikaži" },
    mk: { filters: "Филтри", clearAll: "Исчисти сè", clear: "Исчисти", showMore: "Прикажи повеќе", showLess: "Прикажи помалку", searchValues: "Пребарај…", close: "Затвори", from: "Од", to: "До", inStock: "На залиха", show: "Прикажи" },
    pl: { filters: "Filtry", clearAll: "Wyczyść wszystko", clear: "Wyczyść", showMore: "Pokaż więcej", showLess: "Pokaż mniej", searchValues: "Szukaj…", close: "Zamknij", from: "Od", to: "Do", inStock: "Dostępny", show: "Pokaż" },
  };
  var M2L = { IE: "en", NL: "nl", AT: "de", DE: "de", SK: "sk", SI: "sl", GR: "el", HR: "hr", IT: "it", BG: "bg", CZ: "cs", HU: "hu", RO: "ro", RS: "sr", BA: "hr", MK: "mk", PL: "pl" };
  var T = I18N[M2L[CC] || "de"] || I18N.de;
  var CUR = { CZ: "Kč", HU: "Ft", RO: "lei", RS: "din", BA: "KM", MK: "den", PL: "zł" };
  var CCY = CUR[CC] || "€";

  // ── Hide native facet UI (only inside the facets column + the pills bar); grid untouched ──
  var killCss = document.createElement("style");
  killCss.setAttribute("data-amh-filter", "");
  // Hides: the facets column content (we sit there), the pills bar, the legacy accordion, AND the
  // native mobile toggle + its #filters-drawer dialog — the dialog would otherwise open with
  // hidden content = an invisible modal that traps every tap ("site frozen" bug).
  var KILL_SEL = CFG.killSel || "filters-pills-menu,.artmie-filters,#artmie-filters,.artmie-active-filters-bar,#artmie-filter-tags-main,.facets-toggle,#filters-drawer";
  // The head guard reserves a 42 px slot for our phone button (no layout shift when it arrives);
  // pin the button to exactly that height so the swap is pixel-neutral on every theme font.
  killCss.textContent = FACETS_SEL + ">*:not([data-amh-filter-root]){display:none!important}" + KILL_SEL + "{display:none!important}" +
    "@media(max-width:989px){[data-amh-filter-btn]{height:42px;vertical-align:top}}";
  // appended in activate(), removed in deactivate() — collection pages only

  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"']/g, function (ch) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]; }); }

  // ── Shared styles for both shadow roots ──
  var TREE_CSS =
    "*{box-sizing:border-box;font-family:Poppins,Inter,system-ui,-apple-system,sans-serif}" +
    ".chips{display:flex;flex-wrap:wrap;gap:8px;margin:0 0 12px}" +
    ".chip{display:inline-flex;align-items:center;gap:7px;background:#f4f4f4;border:1px solid #e6e6e6;border-radius:999px;padding:6px 8px 6px 12px;font-size:12.5px;color:#1a1a1a}" +
    ".chip b{font-weight:600}.chip button{border:none;background:#dcdcdc;color:#1a1a1a;width:17px;height:17px;border-radius:50%;cursor:pointer;font-size:12px;line-height:1;display:flex;align-items:center;justify-content:center;padding:0}" +
    ".chip button:hover{background:#1a1a1a;color:#fff}" +
    ".clearall{border:none;background:none;color:#767676;font-size:12.5px;font-weight:600;cursor:pointer;text-decoration:underline;font-family:inherit;padding:6px 2px}" +
    ".grp{border-bottom:1px solid #ececec;padding:14px 0 12px}" +
    ".grp .hd{display:flex;align-items:center;justify-content:space-between;cursor:pointer;font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:.4px;color:#1a1a1a;background:none;border:none;width:100%;padding:0;font-family:inherit}" +
    ".grp .hd .car{transition:transform .15s;color:#8a8a8a}" +
    ".grp.closed .car{transform:rotate(-90deg)}.grp.closed .bd{display:none}" +
    ".bd{margin-top:10px}" +
    ".vsearch{width:100%;border:1px solid #dcdcdc;border-radius:8px;padding:7px 10px;font-size:13px;margin-bottom:8px;font-family:inherit;outline:none}.vsearch:focus{border-color:#1a1a1a}" +
    ".vlist{display:flex;flex-direction:column;gap:2px}" +
    ".v{display:flex;align-items:center;gap:9px;padding:5px 2px;border:none;background:none;cursor:pointer;font-family:inherit;font-size:13.5px;color:#3a3a3a;text-align:left;width:100%;border-radius:6px}" +
    ".v:hover{background:#f6f6f6}" +
    ".cb{width:17px;height:17px;border:1.5px solid #c9c9c9;border-radius:4px;flex:none;display:flex;align-items:center;justify-content:center;background:#fff;transition:background .1s,border-color .1s}" +
    ".v.sel .cb{background:#1a1a1a;border-color:#1a1a1a}.v.sel .cb svg{display:block}.cb svg{display:none;width:11px;height:11px;stroke:#fff;stroke-width:3;fill:none}" +
    ".v .n{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.v .c{color:#9a9a9a;font-size:12px;flex:none}" +
    ".sw{width:18px;height:18px;border-radius:50%;border:1px solid rgba(0,0,0,.15);flex:none;background-size:cover;background-position:center}" +
    ".v.sel .sw{outline:2px solid #1a1a1a;outline-offset:1.5px}" +
    ".morev{border:none;background:none;color:#1a1a1a;font-size:12.5px;font-weight:600;cursor:pointer;padding:6px 2px;font-family:inherit;text-decoration:underline}" +
    ".rng{padding:6px 2px 2px}" +
    ".rrow{position:relative;height:26px}" +
    ".rrow input[type=range]{position:absolute;left:0;right:0;top:0;width:100%;-webkit-appearance:none;appearance:none;background:none;pointer-events:none;height:26px;margin:0}" +
    ".rrow input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;pointer-events:auto;width:18px;height:18px;border-radius:50%;background:#fff;border:2px solid #1a1a1a;cursor:pointer;margin-top:-7.5px}" +
    ".rrow input[type=range]::-moz-range-thumb{pointer-events:auto;width:15px;height:15px;border-radius:50%;background:#fff;border:2px solid #1a1a1a;cursor:pointer}" +
    ".rrow input[type=range]::-webkit-slider-runnable-track{height:3px;background:none}" +
    ".rtrack{position:absolute;top:11.5px;left:0;right:0;height:3px;background:#e6e6e6;border-radius:2px}.rfill{position:absolute;top:11.5px;height:3px;background:#1a1a1a;border-radius:2px}" +
    ".rio{display:flex;align-items:center;gap:8px;margin-top:8px}" +
    ".rio input{width:100%;border:1px solid #dcdcdc;border-radius:8px;padding:7px 9px;font-size:13px;font-family:inherit;outline:none}.rio input:focus{border-color:#1a1a1a}.rio span{color:#9a9a9a;font-size:12px}" +
    // skeleton (first-ever visit, before the tree arrives — the column is never empty)
    ".skg{border-bottom:1px solid #ececec;padding:16px 0 14px}" +
    ".skb{height:12px;border-radius:6px;background:linear-gradient(100deg,#f1f1f1 40%,#e7e7e7 50%,#f1f1f1 60%);background-size:200% 100%;animation:amhsh 1.1s infinite;margin-bottom:9px}" +
    ".skb--t{width:45%;height:13px;margin-bottom:13px}.skb--s{width:70%}" +
    "@keyframes amhsh{to{background-position:-200% 0}}";

  // Sidebar host — lives INSIDE the theme's facets column (keeps the native layout/column width).
  var sideHost = document.createElement("div");
  sideHost.setAttribute("data-amh-filter-root", "");
  sideHost.style.cssText = "display:block;width:100%";
  var sideRoot = sideHost.attachShadow({ mode: "open" });
  // padding-bottom keeps the LAST filter groups scrollable clear of bottom-anchored floating
  // bubbles (games launcher, chat). Mobile (≤989px) never shows the inline sidebar — drawer only.
  sideRoot.innerHTML = "<style>:host{all:initial;display:block}" + TREE_CSS + ".side{position:sticky;top:12px;padding-bottom:110px}@media(max-width:989px){.side{display:none}}</style>" + '<div class="side"><div class="chips"></div><div class="tree"></div></div>';
  // appended into the facets column in activate()

  // Fixed host — mobile "Filtri" button + drawer (on <body>, independent of the section swaps).
  var fixedHost = document.createElement("div");
  fixedHost.setAttribute("data-amh-filter-fixed", "");
  fixedHost.style.display = "none"; // shown in activate() — NEVER on product/other pages
  document.body.appendChild(fixedHost);
  var fixedRoot = fixedHost.attachShadow({ mode: "open" });
  fixedRoot.innerHTML =
    "<style>:host{all:initial}" + TREE_CSS +
    ".dov{position:fixed;inset:0;z-index:2147483002;display:none}.dov.open{display:block}.dback{position:absolute;inset:0;background:rgba(0,0,0,.42)}" +
    ".drawer{position:absolute;top:0;left:0;bottom:0;width:min(360px,92vw);background:#fff;box-shadow:8px 0 40px rgba(0,0,0,.2);display:flex;flex-direction:column}" +
    ".dhd{display:flex;align-items:center;justify-content:space-between;padding:16px 18px;border-bottom:1px solid #ececec;font-size:15px;font-weight:700;font-family:Poppins,Inter,sans-serif}" +
    ".dhd button{border:none;background:none;font-size:24px;color:#767676;cursor:pointer;line-height:1}" +
    ".dbody{flex:1;overflow-y:auto;padding:2px 18px 130px}" + // 130px: keep the last groups clear of floating bubbles
    ".dft{padding:12px 18px;border-top:1px solid #ececec;background:#fff}.dapply{width:100%;border:none;background:#1a1a1a;color:#fff;font-size:14px;font-weight:700;padding:14px;border-radius:10px;cursor:pointer;font-family:inherit}" +
    "</style>" +
    '<div class="dov"><div class="dback"></div><div class="drawer"><div class="dhd"><span>' + esc(T.filters) + '</span><button type="button" aria-label="' + esc(T.close) + '">×</button></div><div class="dbody"><div class="chips"></div><div class="tree"></div></div><div class="dft"><button class="dapply" type="button">' + esc(T.show) + "</button></div></div></div>";

  var dov = fixedRoot.querySelector(".dov");

  // Inline "Filtri" button — replaces the native mobile "Filtriraj" toggle IN PLACE in the
  // toolbar (never floats over content: a floating button covered the products' add-to-cart).
  var btnHost = document.createElement("div");
  btnHost.setAttribute("data-amh-filter-btn", "");
  btnHost.style.cssText = "display:inline-block";
  var btnRoot = btnHost.attachShadow({ mode: "open" });
  btnRoot.innerHTML =
    "<style>:host{all:initial;display:inline-block}*{box-sizing:border-box;font-family:Poppins,Inter,system-ui,sans-serif}" +
    ".ibtn{display:none;border:none;background:#1a1a1a;color:#fff;font-size:13.5px;font-weight:600;padding:11px 18px;border-radius:999px;cursor:pointer;align-items:center;gap:8px}" +
    "@media(max-width:989px){.ibtn{display:inline-flex}}" +
    ".ibtn .bub{background:#fff;color:#1a1a1a;border-radius:999px;font-size:11px;min-width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;padding:0 5px;font-weight:700}" +
    "</style>" +
    '<button class="ibtn" type="button">' + esc(T.filters) + ' <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 4H3l6 8.5V21l6-2v-6.5L21 4Z"/></svg><span class="bub" style="display:none"></span></button>';
  var fbtn = btnRoot.querySelector(".ibtn"), fBub = btnRoot.querySelector(".bub");
  function mountBtn() {
    var toggle = document.querySelector(".facets-toggle");
    var target = toggle ? toggle.parentNode : sectionEl;
    if (!target) return;
    if (!btnHost.parentNode || btnHost.parentNode !== target) {
      if (toggle) target.insertBefore(btnHost, toggle);
      else sectionEl.insertBefore(btnHost, sectionEl.firstChild);
    }
    btnHost.style.display = "inline-block";
  }

  // ── State: selection keyed by NATIVE param identity ("price" | "availability" | "ns.key") ──
  var state = { filters: {}, tree: [], openGroups: {}, expanded: {}, vquery: {} };

  function parseUrl() {
    state.filters = {};
    var sp = new URLSearchParams(location.search);
    sp.forEach(function (val, key) {
      if (key === "filter.v.price.gte") { (state.filters.price = state.filters.price || {}).min = parseFloat(val); return; }
      if (key === "filter.v.price.lte") { (state.filters.price = state.filters.price || {}).max = parseFloat(val); return; }
      if (key === "filter.v.availability") { state.filters.availability = true; return; }
      var mm = key.match(/^filter\.p\.m\.([^.]+)\.(.+)$/);
      if (mm) {
        var k = mm[1] + "." + mm[2];
        (state.filters[k] = state.filters[k] || []).push(val);
      }
    });
  }

  /**
   * The inverse of nativeValuesFor. A shared URL carries the EXPANDED members — one "sada" tick
   * leaves ?…balenie=sada&…balenie=sada+6+ks&…balenie=sada+12+ks — so reading it straight back
   * would tick three boxes and draw three chips for one choice the shopper made once.
   *
   * Runs only once the tree is known: parseUrl() fires before the first fetch, so this is called
   * again after the tree lands (and after a cached tree is restored). Unknown values pass through
   * untouched, which is what keeps a hand-edited or legacy URL working.
   */
  function foldSelection() {
    if (!state.tree || !state.tree.length) return;
    Object.keys(state.filters).forEach(function (param) {
      var vals = state.filters[param];
      if (!Array.isArray(vals)) return;
      var out = [];
      vals.forEach(function (raw) {
        var canon = raw;
        for (var i = 0; i < state.tree.length && canon === raw; i++) {
          var o = state.tree[i];
          if (o.nativeParam !== param || !o.values) continue;
          for (var j = 0; j < o.values.length; j++) {
            var tv = o.values[j];
            if (tv.value === raw) { canon = tv.value; break; }
            if (tv.members && tv.members.indexOf(raw) !== -1) { canon = tv.value; break; }
          }
        }
        if (out.indexOf(canon) === -1) out.push(canon);
      });
      state.filters[param] = out;
    });
  }
  /**
   * One display value can stand for several raw values — "sada" covers "sada 6 ks", "sada 12 ks"
   * and the rest. Typesense folds them for the sidebar COUNT, but the grid below is rendered by
   * Shopify, which matches the literal string it is given. So a folded value has to be expanded
   * back into every member here, as repeated params: Shopify ORs values inside one filter group.
   *
   * Measured 2026-09-20 on /collections/maliarske-platna — filter.p.m.custom.format=20x20 cm
   * returned ONLY the 8 products holding that exact string, never the canvas whose comma-joined
   * value contains it. Send the canonical alone and the sidebar promises 2,796 while the grid
   * delivers 862.
   *
   * Falls back to the value itself, so an option with no members behaves exactly as before.
   */
  function nativeValuesFor(param, val) {
    for (var i = 0; i < state.tree.length; i++) {
      var o = state.tree[i];
      if (o.nativeParam !== param || !o.values) continue;
      for (var j = 0; j < o.values.length; j++) {
        if (o.values[j].value !== val) continue;
        var m = o.values[j].members;
        return m && m.length ? m : [val];
      }
    }
    return [val];
  }

  function buildQs(filters) {
    var sp = new URLSearchParams(location.search);
    var del = [];
    sp.forEach(function (v, k) { if (k.indexOf("filter.") === 0 || k === "page") del.push(k); });
    del.forEach(function (k) { sp.delete(k); });
    Object.keys(filters).forEach(function (k) {
      var v = filters[k];
      if (k === "price") {
        if (v.min != null && isFinite(v.min)) sp.set("filter.v.price.gte", String(v.min));
        if (v.max != null && isFinite(v.max)) sp.set("filter.v.price.lte", String(v.max));
      } else if (k === "availability") {
        sp.set("filter.v.availability", "1");
      } else if (Array.isArray(v)) {
        v.forEach(function (val) {
          nativeValuesFor(k, val).forEach(function (raw) { sp.append("filter.p.m." + k, raw); });
        });
      }
    });
    return sp.toString();
  }
  function writeUrl() {
    var qs = buildQs(state.filters);
    history.pushState(null, "", location.pathname + (qs ? "?" + qs : ""));
  }

  // ── Data ──
  var booted = false, seq = 0;
  function restoreNative() {
    disabled = true; // API unreachable — stay native for the rest of this page's life
    deactivate();
  }
  function cacheKey() { return "amhF1:" + market + ":" + (COLLECTION || ""); }
  function fetchTree() {
    if (!active) return Promise.resolve();
    var my = ++seq;
    // The head snippet starts a tree fetch DURING HTML PARSING (filtered URLs included) —
    // consume it once instead of fetching again, but only if the URL hasn't changed since.
    var early = null;
    if (window.__amhTreeP && window.__amhTreeQS === location.search) { early = window.__amhTreeP; window.__amhTreeP = null; }
    var req = early || fetch(api + "/api/public/filter", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ market: market, collection: COLLECTION, filters: state.filters, tree: true, perPage: 1, session: getSession() }),
    }).then(function (r) { return r.json(); });
    return req.then(function (r) {
      if (my !== seq) return;
      if (!r || !r.ok || r.fallback) { if (!booted) restoreNative(); return; }
      // An EMPTY tree on first load (e.g. this collection isn't in the index yet) must fall back
      // to the native filter — an empty sidebar with natives hidden means NO filter at all.
      if (!booted && (!r.tree || !r.tree.length)) { restoreNative(); return; }
      booted = true;
      state.tree = r.tree || [];
      foldSelection(); // a shared URL carries expanded members — collapse them before drawing
      renderAll();
      try { localStorage.setItem(cacheKey(), JSON.stringify({ t: Date.now(), tree: state.tree })); } catch (e) { /* full/blocked */ }
      cacheRenderedHtml();
    }).catch(function () { if (!booted) restoreNative(); });
  }
  /** Save the rendered sidebar HTML so the HEAD snippet can paint it INSTANTLY next visit
   *  (before any script executes). Pruned to the 8 most recent collections. */
  function cacheRenderedHtml() {
    try {
      var t = sideRoot.querySelector(".side .tree");
      if (!t || !t.innerHTML || !COLLECTION) return;
      localStorage.setItem("amhH1:" + market + ":" + COLLECTION, JSON.stringify({ t: Date.now(), html: "<style>" + TREE_CSS + "</style><div style=\"padding-bottom:110px\">" + t.innerHTML + "</div>" }));
      var keys = [];
      for (var i = 0; i < localStorage.length; i++) { var k = localStorage.key(i); if (k && k.indexOf("amhH1:") === 0) keys.push(k); }
      if (keys.length > 8) {
        keys.map(function (k) { try { return { k: k, t: (JSON.parse(localStorage.getItem(k)) || {}).t || 0 }; } catch (e) { return { k: k, t: 0 }; } })
          .sort(function (a, b) { return a.t - b.t; })
          .slice(0, keys.length - 8)
          .forEach(function (x) { localStorage.removeItem(x.k); });
      }
    } catch (e) { /* full/blocked */ }
  }

  /** Instant first paint of the sidebar: yesterday's tree from cache, else a skeleton. */
  function renderInstant() {
    try {
      var c = JSON.parse(localStorage.getItem(cacheKey()) || "0");
      if (c && c.tree && Date.now() - c.t < 86400000) { state.tree = c.tree; foldSelection(); renderAll(); return; }
    } catch (e) { /* ignore */ }
    var h = "";
    for (var i = 0; i < 5; i++) h += '<div class="skg"><div class="skb skb--t"></div><div class="skb"></div><div class="skb skb--s"></div></div>';
    var t = sideRoot.querySelector(".side .tree");
    if (t) t.innerHTML = h;
  }
  // Swap the native section through the Section Rendering API — the grid stays the theme's own.
  // Shopify's server render of a big collection section takes ~1-2s, so: (a) a NEW click ABORTS
  // the in-flight one (latest wins — no stale swaps, no wasted renders on rapid clicking);
  // (b) a spinner gives immediate feedback; (c) sessionStorage caches each rendered state so
  // toggling BACK to a previous selection is instant.
  var sectionCtl = null;
  function spinTarget() { return (sectionEl && sectionEl.querySelector(RESULTS_SEL)) || sectionEl; }
  function gridSpinner(on) {
    var sp = document.getElementById("amh-grid-spin");
    var t = spinTarget();
    if (!on) { if (sp) sp.parentNode.removeChild(sp); if (t) t.style.opacity = ""; if (sectionEl) sectionEl.style.opacity = ""; return; }
    if (!t) return;
    t.style.opacity = "0.55";
    if (sp) return;
    sp = document.createElement("div");
    sp.id = "amh-grid-spin";
    sp.innerHTML = '<style>#amh-grid-spin{position:absolute;left:50%;transform:translateX(-50%);margin-top:120px;z-index:10}#amh-grid-spin i{display:block;width:34px;height:34px;border:3px solid #dcdcdc;border-top-color:#1a1a1a;border-radius:50%;animation:amhsp .7s linear infinite}@keyframes amhsp{to{transform:rotate(360deg)}}</style><i></i>';
    var anchor = t.parentElement || sectionEl;
    if (getComputedStyle(anchor).position === "static") anchor.style.position = "relative";
    anchor.insertBefore(sp, anchor.firstChild);
  }
  // Horizon hides late-inserted grid items until ITS OWN JS animates them in:
  //   results-list[initialized] .product-grid__item { opacity:0; transform:translateY(10px) }
  // Items we swap in never get that animation (the theme's observer only knows its own
  // inserts) and stay invisible — "blank spaces where products used to be". Reveal them
  // ourselves, with the same slide-in so it feels native.
  function revealCards(scope) {
    var items = scope.querySelectorAll(".product-grid__item");
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      it.style.opacity = "1";
      it.style.transform = "none";
      // No entrance animation: a web-animation here (fill:backwards + delay) was observed
      // STUCK at progress 0 on jank-heavy swaps — animations beat inline styles in the
      // cascade, so the cards stayed invisible. Instant is also simply faster. Cancel any
      // animation something else may have parked on the item for the same reason.
      try { if (it.getAnimations) it.getAnimations().forEach(function (a) { a.cancel(); }); } catch (e) { /* fine */ }
    }
  }
  // THEME BUG GUARD (colour-filter pages): with a colour filter applied, Horizon's card JS
  // activates the slide of the colour-matching variant. When that variant has no own image
  // it finds nothing and leaves EVERY slide aria-hidden="true" → a blank card holding its
  // grid slot ("gaps" between products). Reproduced on farby-na-povrchy?farba=ružová on a
  // plain server-rendered load — not caused by our swaps. If a gallery ends up with no
  // active slide, activate its first one.
  function fixBlankCards() {
    if (!sectionEl) return;
    // Collapse grid slots whose product-card some script hid: the legacy artmie-vsf script
    // (variant stock filter) sets data-artmie-vsf-oos + display:none on cards whose
    // colour-matched variant is out of stock — but in the Horizon grid the card sits INSIDE
    // <li class="product-grid__item">, so hiding only the card leaves an empty slot = the
    // "gaps between products". Mirror the card's visibility onto its grid item.
    var items = sectionEl.querySelectorAll(".product-grid__item");
    for (var x = 0; x < items.length; x++) {
      var pc = items[x].querySelector("product-card");
      if (!pc) continue;
      var want = getComputedStyle(pc).display === "none" ? "none" : "";
      if (items[x].style.display !== want) items[x].style.display = want;
    }
    var shows = sectionEl.querySelectorAll(".card-gallery slideshow-component");
    for (var i = 0; i < shows.length; i++) {
      var slides = shows[i].querySelectorAll("slideshow-slide");
      if (!slides.length) continue;
      var any = false;
      for (var j = 0; j < slides.length; j++) { if (slides[j].getAttribute("aria-hidden") !== "true") { any = true; break; } }
      if (!any) slides[0].setAttribute("aria-hidden", "false");
    }
  }
  var fixT = null;
  function scheduleBlankFix() {
    clearTimeout(fixT);
    fixT = setTimeout(fixBlankCards, 120); // let the theme's own slide activation run first
  }
  // The theme (re)sets aria-hidden whenever cards init or hover-preview resets — re-check
  // after every such wave. Our own fix sets aria-hidden too, but a gallery with an active
  // slide is a no-op on the next pass, so this settles immediately.
  new MutationObserver(scheduleBlankFix).observe(document.body, { attributes: true, attributeFilter: ["aria-hidden", "data-artmie-vsf-oos"], subtree: true });
  function applySectionHtml(html, fromCache) {
    var doc = new DOMParser().parseFromString(html, "text/html");
    var fresh = doc.getElementById(sectionEl.id) || doc.body.firstElementChild;
    if (!fresh) return null;
    // SURGICAL swap: replace only the results-list contents. Replacing the whole section
    // (a) re-instantiates the theme's <results-list> custom element, whose init re-fetches
    // the section = a SECOND ~800ms server render per click, and (b) re-parses ~750KB of
    // hidden native facets markup (sidebar + mobile drawer render twice per section).
    // Both measured on artmie.sk 2026-07-12. Full swap only as a fallback (e.g. the
    // zero-results empty state has no results list).
    var live = sectionEl.querySelector(RESULTS_SEL);
    var freshList = fresh.querySelector(RESULTS_SEL);
    var compact = null;
    if (live && freshList) {
      live.innerHTML = freshList.innerHTML;
      revealCards(live);
      // Compact form for the sessionStorage cache: the full section is ~1MB in big
      // collections (mostly hidden facets), which would blow the ~5MB quota after a few
      // entries; the results fragment alone is ~1/3 the size.
      compact = "<div>" + freshList.outerHTML + "</div>";
    } else if (!fromCache) {
      // Full swap — only for genuine network renders (a cached fragment would nuke the
      // section chrome incl. the facets column; the network refresh follows anyway).
      sectionEl.innerHTML = fresh.innerHTML;
      revealCards(sectionEl);
      var col = facetsCol();
      if (col && !col.contains(sideHost)) col.appendChild(sideHost); // shadow content survives the move
    } else {
      return null;
    }
    mountBtn();
    scheduleBlankFix();
    return compact || html;
  }
  function sectionUrl(qs) {
    return location.pathname + "?" + (qs ? qs + "&" : "") + "section_id=" + encodeURIComponent(SECTION_ID);
  }
  function saveSectionCache(cacheK, html) {
    try {
      sessionStorage.setItem(cacheK, html);
      var ks = []; for (var i = 0; i < sessionStorage.length; i++) { var k = sessionStorage.key(i); if (k && k.indexOf("amhS1:") === 0) ks.push(k); }
      while (ks.length > 6) sessionStorage.removeItem(ks.shift());
    } catch (e) { /* quota */ }
  }
  // HOVER PREFETCH: the section render is server-bound (~0.8-1.4s on big collections), so we
  // start it the moment the cursor enters a value (pointerenter fires ~200-400ms before the
  // click; pointerdown covers touch). By click time the render is inflight or done.
  var prefetches = {}; // sectionUrl -> { t, html, p }
  window.__amhDbg = { enter: 0, pf: 0, pre: 0, fetch: 0 };
  function prefetchSection(qs) {
    window.__amhDbg.pre++;
    if (!active || !sectionEl) return;
    var url = sectionUrl(qs);
    var pre = prefetches[url];
    if (pre && Date.now() - pre.t < 60000) return; // fresh or inflight
    try { if (sessionStorage.getItem("amhS1:" + url)) return; } catch (e) { /* ignore */ }
    var ks = Object.keys(prefetches);
    if (ks.length > 8) delete prefetches[ks[0]];
    var entry = { t: Date.now(), html: null, p: null };
    window.__amhDbg.fetch++;
    entry.p = fetch(url).then(function (r) { return r.text(); }).then(function (h) { entry.html = h; return h; }).catch(function () { delete prefetches[url]; return null; });
    prefetches[url] = entry;
  }
  function fetchSection() {
    if (!active || !sectionEl) return Promise.resolve();
    var url = sectionUrl(location.search ? location.search.slice(1) : "");
    var cacheK = "amhS1:" + url;
    var cached = null;
    try { cached = sessionStorage.getItem(cacheK); } catch (e) { /* ignore */ }
    if (sectionCtl && sectionCtl.abort) sectionCtl.abort();
    var cachedApplied = cached && applySectionHtml(cached, true);
    // A fresh prefetch (hover/pointerdown) replaces the network round-trip entirely.
    var pre = prefetches[url];
    if (pre && Date.now() - pre.t < 60000) {
      var tok = {}; sectionCtl = tok;
      if (!cachedApplied && !pre.html) gridSpinner(true);
      return (pre.html ? Promise.resolve(pre.html) : pre.p).then(function (html) {
        if (tok !== sectionCtl || !html) return;
        var compact = applySectionHtml(html);
        gridSpinner(false);
        saveSectionCache(cacheK, compact || html);
      });
    }
    var ctl = new AbortController();
    sectionCtl = ctl;
    if (!cachedApplied) gridSpinner(true); // instant toggle-back; still refresh below
    return fetch(url, { signal: ctl.signal }).then(function (r) { return r.text(); }).then(function (html) {
      if (ctl !== sectionCtl) return; // superseded by a newer click
      var compact = applySectionHtml(html);
      gridSpinner(false);
      saveSectionCache(cacheK, compact || html);
    }).catch(function (e) {
      if (e && e.name === "AbortError") return;
      gridSpinner(false);
    });
  }
  function applyChange() {
    writeUrl();
    fetchSection();
    fetchTree();
  }
  window.addEventListener("popstate", function () { syncToLocation(); });

  // If the theme morphs/re-renders the section on its own, put our host back.
  new MutationObserver(function () {
    if (!active) return;
    var col = facetsCol();
    if (col && !col.contains(sideHost)) col.appendChild(sideHost);
    if (!document.contains(btnHost)) mountBtn();
  }).observe(document.body, { childList: true, subtree: true });

  // ── Lifecycle: the theme navigates SOFTLY between pages (morph/view transitions), so the
  // widget must attach on collection pages and fully retreat everywhere else (a floating
  // "Filtri" button has no business on a product page).
  function activate() {
    var col = collectionFromPath();
    var results = document.querySelector(RESULTS_SEL);
    var sec = results ? results.closest('[id^="shopify-section-"]') : null;
    var fcol = facetsCol();
    if (!col || !sec || !fcol) { deactivate(); return; }
    COLLECTION = col;
    sectionEl = sec;
    SECTION_ID = sec.id.slice("shopify-section-".length);
    // The head guard hid the native facets at FIRST PAINT; take over from it seamlessly
    // and tell its watchdog we're alive (otherwise it un-hides the native filter after 4s).
    window.__amhFilterOn = true;
    if (!killCss.parentNode) document.head.appendChild(killCss);
    var guard = document.getElementById("amh-filter-guard");
    if (guard && guard !== killCss && guard.parentNode) guard.parentNode.removeChild(guard);
    if (!fcol.contains(sideHost)) fcol.appendChild(sideHost);
    var inst = document.getElementById("amh-instant"); // head snippet's instant paint — we take over
    if (inst && inst.parentNode) inst.parentNode.removeChild(inst);
    sideHost.style.display = "block";
    fixedHost.style.display = "";
    active = true;
    mountBtn();
    parseUrl();
    renderInstant(); // cached tree or skeleton — the column is never empty
    fetchTree();
    // Initial server-rendered load: blank colour-filter cards need the guard too, and the
    // theme's card init may not have run yet — check a few times as it settles.
    scheduleBlankFix();
    setTimeout(fixBlankCards, 800);
    setTimeout(fixBlankCards, 2000);
  }
  function deactivate() {
    active = false;
    COLLECTION = null;
    sectionEl = null;
    try {
      if (killCss.parentNode) killCss.parentNode.removeChild(killCss);
      var guard = document.getElementById("amh-filter-guard");
      if (guard && guard.parentNode) guard.parentNode.removeChild(guard);
      sideHost.style.display = "none";
      fixedHost.style.display = "none";
      btnHost.style.display = "none";
      closeDrawer();
    } catch (e) { /* ignore */ }
  }
  function syncToLocation() {
    if (disabled) return;
    if (collectionFromPath()) {
      if (!active || collectionFromPath() !== COLLECTION) activate();
      else { parseUrl(); fetchSection(); fetchTree(); } // same page, changed params (back/forward)
    } else {
      deactivate();
    }
  }
  // Watch for soft navigations the theme performs outside popstate.
  var lastHref = location.href;
  setInterval(function () {
    if (location.href === lastHref) return;
    var pathChanged = location.pathname !== lastHref.split("?")[0].replace(/^https?:\/\/[^/]+/, "");
    lastHref = location.href;
    if (pathChanged && !disabled) syncToLocation();
  }, 400);

  // ── Selection helpers (keyed by option.nativeParam) ──
  // Pure: what the selection WOULD be after toggling — used by the click itself and by
  // hover prefetch (which must not mutate state).
  function toggledFilters(opt, value) {
    var f = {};
    Object.keys(state.filters).forEach(function (k) { f[k] = Array.isArray(state.filters[k]) ? state.filters[k].slice() : state.filters[k]; });
    var key = opt.nativeParam;
    if (key === "availability") {
      if (f.availability) delete f.availability; else f.availability = true;
    } else {
      var arr = Array.isArray(f[key]) ? f[key].slice() : [];
      var i = arr.indexOf(value);
      if (opt.selectType === "single") arr = i === -1 ? [value] : [];
      else if (i === -1) arr.push(value); else arr.splice(i, 1);
      if (arr.length) f[key] = arr; else delete f[key];
    }
    return f;
  }
  function toggleValue(opt, value) {
    if (!opt.nativeParam) return;
    state.filters = toggledFilters(opt, value);
    applyChange();
  }
  function setRange(min, max, bounds) {
    var r = {};
    if (min != null && isFinite(min) && min > bounds.min) r.min = min;
    if (max != null && isFinite(max) && max < bounds.max) r.max = max;
    if (r.min != null || r.max != null) state.filters.price = r; else delete state.filters.price;
    applyChange();
  }
  function clearAll() { state.filters = {}; applyChange(); }
  function selectedFor(opt) {
    if (!opt.nativeParam) return [];
    if (opt.nativeParam === "availability") return state.filters.availability ? ["true"] : [];
    return Array.isArray(state.filters[opt.nativeParam]) ? state.filters[opt.nativeParam] : [];
  }
  function filterCount() {
    var n = 0;
    for (var k in state.filters) { var v = state.filters[k]; n += Array.isArray(v) ? v.length : 1; }
    return n;
  }

  // ── Rendering (same markup into the sidebar and the drawer) ──
  function groupHtml(o, mobile) {
    if (!o.nativeParam) return ""; // not natively filterable — never show a dead option
    var open = state.openGroups[o.field] !== undefined ? state.openGroups[o.field] : !(mobile ? o.collapsedMobile : o.collapsedPC);
    var h = '<div class="grp' + (open ? "" : " closed") + '" data-f="' + esc(o.field) + '">' +
      '<button class="hd" type="button"' + (o.tooltip ? ' title="' + esc(o.tooltip) + '"' : "") + "><span>" + esc(o.label) + '</span><svg class="car" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="m6 9 6 6 6-6"/></svg></button>' +
      '<div class="bd">';
    if (o.range) {
      var sel = state.filters.price || {};
      var lo = sel.min != null ? sel.min : o.range.min, hi = sel.max != null ? sel.max : o.range.max;
      h += '<div class="rng" data-min="' + o.range.min + '" data-max="' + o.range.max + '">' +
        '<div class="rrow"><div class="rtrack"></div><div class="rfill"></div>' +
        '<input type="range" class="rlo" min="' + o.range.min + '" max="' + o.range.max + '" step="1" value="' + lo + '">' +
        '<input type="range" class="rhi" min="' + o.range.min + '" max="' + o.range.max + '" step="1" value="' + hi + '"></div>' +
        '<div class="rio"><input type="number" class="nlo" min="' + o.range.min + '" max="' + o.range.max + '" value="' + lo + '" aria-label="' + esc(T.from) + '"><span>–</span>' +
        '<input type="number" class="nhi" min="' + o.range.min + '" max="' + o.range.max + '" value="' + hi + '" aria-label="' + esc(T.to) + '"><span>' + esc(CCY) + "</span></div></div>";
    } else if (o.values && o.values.length) {
      var selected = selectedFor(o);
      if (o.searchBox) h += '<input class="vsearch" type="text" placeholder="' + esc(T.searchValues) + '" value="' + esc(state.vquery[o.field] || "") + '">';
      var vq = (state.vquery[o.field] || "").toLowerCase();
      var vals = o.values;
      if (vq) vals = vals.filter(function (v) { return v.value.toLowerCase().indexOf(vq) !== -1; });
      var expanded = state.expanded[o.field];
      var maxN = expanded ? vals.length : Math.max(o.maxVisible || 8, 4);
      h += '<div class="vlist">';
      for (var i = 0; i < vals.length && i < maxN; i++) {
        var v = vals[i];
        var isSel = selected.indexOf(v.value) !== -1;
        var name = o.nativeParam === "availability" ? T.inStock : v.value;
        var swatch = "";
        if (o.display === "swatch" || v.swatch) {
          var st = v.swatch && v.swatch.image ? "background-image:url('" + esc(v.swatch.image) + "')" : "background:" + esc((v.swatch && v.swatch.hex) || "#ddd");
          swatch = '<span class="sw" style="' + st + '"></span>';
        }
        h += '<button class="v' + (isSel ? " sel" : "") + '" type="button" data-v="' + esc(v.value) + '">' +
          (swatch || '<span class="cb"><svg viewBox="0 0 24 24"><path d="m5 13 4 4L19 7"/></svg></span>') +
          '<span class="n">' + esc(name) + '</span><span class="c">' + v.count + "</span></button>";
        if (o.nativeParam === "availability") break; // one "in stock" toggle, not true/false rows
      }
      h += "</div>";
      if (vals.length > maxN) h += '<button class="morev" type="button">' + esc(T.showMore) + " (" + (vals.length - maxN) + ")</button>";
      else if (expanded && vals.length > (o.maxVisible || 8)) h += '<button class="morev" type="button">' + esc(T.showLess) + "</button>";
    } else {
      return "";
    }
    return h + "</div></div>";
  }

  function chipsHtml() {
    var h = "";
    state.tree.forEach(function (o) {
      if (!o.nativeParam) return;
      if (o.range && state.filters.price) {
        var p = state.filters.price;
        h += '<span class="chip">' + esc(o.label) + ': <b>' + esc((p.min != null ? p.min : "") + "–" + (p.max != null ? p.max : "") + " " + CCY) + '</b><button type="button" data-k="price" aria-label="' + esc(T.clear) + '">×</button></span>';
        return;
      }
      selectedFor(o).forEach(function (val) {
        var name = o.nativeParam === "availability" ? T.inStock : val;
        h += '<span class="chip">' + esc(o.label) + ': <b>' + esc(name) + '</b><button type="button" data-k="' + esc(o.nativeParam) + '" data-v="' + esc(val) + '" aria-label="' + esc(T.clear) + '">×</button></span>';
      });
    });
    if (h) h += '<button class="clearall" type="button">' + esc(T.clearAll) + "</button>";
    return h;
  }

  function bindTree(container, mobile) {
    container.querySelectorAll(".grp").forEach(function (g) {
      var field = g.getAttribute("data-f");
      var opt = null;
      for (var i = 0; i < state.tree.length; i++) if (state.tree[i].field === field) opt = state.tree[i];
      if (!opt) return;
      g.querySelector(".hd").addEventListener("click", function () {
        state.openGroups[field] = g.classList.contains("closed"); // toggling below
        g.classList.toggle("closed");
        state.openGroups[field] = !g.classList.contains("closed");
      });
      g.querySelectorAll("[data-v]").forEach(function (b) {
        b.addEventListener("click", function () { toggleValue(opt, b.getAttribute("data-v")); });
        // Prefetch the would-be section render on hover intent (and pointerdown for touch,
        // which fires ~100ms before click) — by click time it's inflight or already here.
        // The 100ms dwell keeps a cursor sweep down the list from burst-rendering.
        var pf = function () { window.__amhDbg.pf++; if (opt.nativeParam) prefetchSection(buildQs(toggledFilters(opt, b.getAttribute("data-v")))); };
        var pfT = null;
        b.addEventListener("pointerenter", function () { window.__amhDbg.enter++; clearTimeout(pfT); pfT = setTimeout(pf, 100); });
        b.addEventListener("pointerleave", function () { clearTimeout(pfT); });
        b.addEventListener("pointerdown", function () { clearTimeout(pfT); pf(); }, { passive: true });
      });
      var mv = g.querySelector(".morev");
      if (mv) mv.addEventListener("click", function () { state.expanded[field] = !state.expanded[field]; renderAll(); });
      var vs = g.querySelector(".vsearch");
      if (vs) vs.addEventListener("input", function () {
        state.vquery[field] = vs.value;
        renderAll();
        var again = container.querySelector('.grp[data-f="' + field + '"] .vsearch');
        if (again) { again.focus(); again.setSelectionRange(again.value.length, again.value.length); }
      });
      var rng = g.querySelector(".rng");
      if (rng) bindRange(rng);
    });
    var chips = container.parentNode ? container.parentNode.querySelector(".chips") : null;
    if (chips) {
      chips.querySelectorAll(".chip button").forEach(function (b) {
        b.addEventListener("click", function () {
          var k = b.getAttribute("data-k"), v = b.getAttribute("data-v");
          if (k === "price" || k === "availability" || v == null) delete state.filters[k];
          else {
            var arr = (state.filters[k] || []).filter(function (x) { return x !== v; });
            if (arr.length) state.filters[k] = arr; else delete state.filters[k];
          }
          applyChange();
        });
      });
      var ca = chips.querySelector(".clearall");
      if (ca) ca.addEventListener("click", clearAll);
    }
  }
  function bindRange(rng) {
    var bounds = { min: parseFloat(rng.getAttribute("data-min")), max: parseFloat(rng.getAttribute("data-max")) };
    var lo = rng.querySelector(".rlo"), hi = rng.querySelector(".rhi"), nlo = rng.querySelector(".nlo"), nhi = rng.querySelector(".nhi");
    var fill = rng.querySelector(".rfill");
    var t = null;
    function paint() {
      var a = parseFloat(lo.value), b = parseFloat(hi.value);
      var p1 = ((a - bounds.min) / (bounds.max - bounds.min)) * 100, p2 = ((b - bounds.min) / (bounds.max - bounds.min)) * 100;
      fill.style.left = Math.min(p1, p2) + "%"; fill.style.right = (100 - Math.max(p1, p2)) + "%";
    }
    function commit() { clearTimeout(t); t = setTimeout(function () { setRange(parseFloat(lo.value), parseFloat(hi.value), bounds); }, 450); }
    function syncFromSlider() {
      if (parseFloat(lo.value) > parseFloat(hi.value)) { var tmp = lo.value; lo.value = hi.value; hi.value = tmp; }
      nlo.value = lo.value; nhi.value = hi.value; paint(); commit();
    }
    lo.addEventListener("input", syncFromSlider); hi.addEventListener("input", syncFromSlider);
    function syncFromNum() {
      var a = parseFloat(nlo.value), b = parseFloat(nhi.value);
      if (!isFinite(a)) a = bounds.min; if (!isFinite(b)) b = bounds.max;
      lo.value = Math.max(bounds.min, Math.min(a, bounds.max)); hi.value = Math.max(bounds.min, Math.min(b, bounds.max));
      paint(); commit();
    }
    nlo.addEventListener("change", syncFromNum); nhi.addEventListener("change", syncFromNum);
    paint();
  }

  function renderInto(root, mobile) {
    var tree = root.querySelector(".tree"), chips = root.querySelector(".chips");
    if (!tree) return;
    var h = "";
    for (var i = 0; i < state.tree.length; i++) h += groupHtml(state.tree[i], mobile);
    tree.innerHTML = h;
    // Chips ONLY in the mobile drawer — on the page the theme renders its own native
    // "FILTERI:" pill bar for the same params (no duplication).
    if (chips) chips.innerHTML = mobile ? chipsHtml() : "";
    bindTree(tree, mobile);
  }
  function renderAll() {
    renderInto(sideRoot.querySelector(".side"), false);
    if (dov.classList.contains("open")) renderInto(fixedRoot.querySelector(".dbody"), true);
    var n = filterCount();
    fBub.style.display = n ? "" : "none";
    fBub.textContent = n;
  }

  // ── Drawer (mobile) — hides the games launcher while open so nothing overlaps the tree ──
  function gameFabs() { return document.querySelectorAll(".amhg-fab"); }
  function openDrawer() {
    renderInto(fixedRoot.querySelector(".dbody"), true);
    dov.classList.add("open");
    document.documentElement.style.overflow = "hidden";
    gameFabs().forEach(function (f) { f.style.visibility = "hidden"; });
  }
  function closeDrawer() {
    dov.classList.remove("open");
    document.documentElement.style.overflow = "";
    gameFabs().forEach(function (f) { f.style.visibility = ""; });
  }
  fbtn.addEventListener("click", openDrawer);
  fixedRoot.querySelector(".dback").addEventListener("click", closeDrawer);
  fixedRoot.querySelector(".dhd button").addEventListener("click", closeDrawer);
  fixedRoot.querySelector(".dapply").addEventListener("click", closeDrawer);

  // ── Session (shared with amh-search so analytics sees one shopper) ──
  function getSession() {
    try {
      var k = "amh_sid", v = localStorage.getItem(k);
      if (!v) { v = String(Date.now()) + Math.random().toString(36).slice(2, 10); localStorage.setItem(k, v); }
      return v;
    } catch (e) { return ""; }
  }

  // ── Boot: the grid is already server-rendered from the URL params — only the tree loads ──
  syncToLocation();
})();
